import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { API, useAuth } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { 
  MapPin, 
  BedDouble, 
  Bath, 
  Square, 
  Heart,
  Share2,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  Calendar,
  Check,
  Home,
  Eye,
  Download,
  Clock,
  Loader2,
  CalendarDays,
  School,
  ShoppingBag,
  Stethoscope,
  Train,
  TreePine,
  Utensils
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Tour scheduling state
  const [tourDialogOpen, setTourDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [bookingTour, setBookingTour] = useState(false);
  const [tourForm, setTourForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: ""
  });
  const [businessSettings, setBusinessSettings] = useState({
    email: "info@buyorent.com",
    phone: "+260 960911896"
  });

  useEffect(() => {
    fetchProperty();
    fetchBusinessSettings();
    if (user) {
      checkSaved();
    }
  }, [id, user]);

  const fetchBusinessSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/business`);
      setBusinessSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Error fetching business settings:", error);
    }
  };

  useEffect(() => {
    if (user) {
      setInquiryForm(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
    }
  }, [user]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error("Error fetching property:", error);
      toast.error("Property not found");
      navigate("/properties");
    } finally {
      setLoading(false);
    }
  };

  const checkSaved = async () => {
    try {
      const response = await axios.get(`${API}/saved-properties`, {
        withCredentials: true
      });
      const saved = response.data.some(p => p.property_id === id);
      setIsSaved(saved);
    } catch (error) {
      // User not logged in or error
    }
  };

  const handleSave = async () => {
    if (!user) {
      login();
      return;
    }

    try {
      if (isSaved) {
        await axios.delete(`${API}/saved-properties/${id}`, {
          withCredentials: true
        });
        setIsSaved(false);
        toast.success("Property removed from saved");
      } else {
        await axios.post(`${API}/saved-properties/${id}`, {}, {
          withCredentials: true
        });
        setIsSaved(true);
        toast.success("Property saved!");
      }
    } catch (error) {
      toast.error("Failed to update saved properties");
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  const handleDownloadImage = async () => {
    const imageUrl = property?.images?.[currentImageIndex];
    if (!imageUrl) {
      toast.error("No image to download");
      return;
    }

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${property.title.replace(/\s+/g, '_')}_image_${currentImageIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Image downloaded!");
    } catch (error) {
      // Fallback: open image in new tab
      window.open(imageUrl, '_blank');
      toast.info("Image opened in new tab");
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        property_id: id,
        ...inquiryForm
      }, { withCredentials: true });
      
      toast.success("Inquiry sent successfully!");
      setInquiryDialogOpen(false);
      setInquiryForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  // Tour scheduling functions
  const fetchBookedDates = async () => {
    try {
      const response = await axios.get(`${API}/tours/property/${id}`);
      setBookedDates(response.data);
    } catch (error) {
      console.error("Error fetching booked dates:", error);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/tours/availability/${id}?date=${dateStr}`);
      setAvailableSlots(response.data.available_slots);
      setBookedSlots(response.data.booked_slots);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
      setBookedSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    if (date) {
      fetchAvailableSlots(date);
    }
  };

  const openTourDialog = () => {
    setTourDialogOpen(true);
    fetchBookedDates();
    // Pre-fill form with user data if logged in
    if (user) {
      setTourForm(prev => ({
        ...prev,
        name: user.name || "",
        email: user.email || ""
      }));
    }
  };

  const handleTourSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    setBookingTour(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.post(`${API}/tours`, {
        property_id: id,
        customer_name: tourForm.name,
        customer_email: tourForm.email,
        customer_phone: tourForm.phone,
        tour_date: dateStr,
        tour_time: selectedTime,
        notes: tourForm.notes
      });
      
      toast.success("Tour scheduled successfully! We'll contact you to confirm.");
      setTourDialogOpen(false);
      setSelectedDate(null);
      setSelectedTime(null);
      setTourForm({ name: "", email: "", phone: "", notes: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule tour");
    } finally {
      setBookingTour(false);
    }
  };

  // Check if a date has any booked slots
  const getDateStatus = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const bookingsOnDate = bookedDates.filter(b => b.tour_date === dateStr);
    if (bookingsOnDate.length >= 9) return "full"; // All 9 slots booked
    if (bookingsOnDate.length > 0) return "partial";
    return "free";
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatPrice = (price, listingType) => {
    if (listingType === "rent") {
      return `K${price.toLocaleString()}/mo`;
    }
    return `K${price.toLocaleString()}`;
  };

  const nextImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === property.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (property?.images?.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? property.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-[500px] bg-muted rounded-sm mb-8"></div>
            <div className="h-8 bg-muted rounded w-1/2 mb-4"></div>
            <div className="h-6 bg-muted rounded w-1/4 mb-8"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20">
        {/* Image Gallery */}
        <section className="relative h-[50vh] md:h-[60vh] bg-muted overflow-hidden">
          <motion.img
            key={currentImageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            src={property.images?.[currentImageIndex] || "https://images.unsplash.com/photo-1746458258536-b9ee5db20a73"}
            alt={property.title}
            className="w-full h-full object-cover"
          />
          
          {/* Gallery Navigation */}
          {property.images?.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white active:scale-90 transition-all"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center hover:bg-white active:scale-90 transition-all"
                data-testid="gallery-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {property.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentImageIndex ? "bg-white" : "bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Back Button */}
          <Link
            to="/properties"
            className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 bg-white/90 rounded-sm text-sm font-medium hover:bg-white transition-colors"
            data-testid="back-to-properties"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>

          {/* Actions */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button
              onClick={handleDownloadImage}
              className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white active:scale-90 transition-all"
              data-testid="download-image-btn"
              title="Download Image"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={handleShare}
              className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white active:scale-90 transition-all"
              data-testid="share-property-btn"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <button
              onClick={handleSave}
              className={`w-10 h-10 rounded-full flex items-center justify-center active:scale-90 transition-all ${
                isSaved 
                  ? "bg-red-500 text-white" 
                  : "bg-white/90 hover:bg-white"
              }`}
              data-testid="save-property-btn"
            >
              <Heart className={`w-5 h-5 ${isSaved ? "fill-current" : ""}`} />
            </button>
          </div>
        </section>

        {/* Property Details */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {property.status === "rented" && property.also_for_sale ? (
                    <>
                      <Badge className="bg-orange-500 text-white hover:bg-orange-600">RENTED</Badge>
                      <Badge className="bg-green-600 text-white hover:bg-green-700">ALSO FOR SALE</Badge>
                    </>
                  ) : (
                    <>
                      <Badge variant={property.listing_type === "rent" ? "secondary" : "default"}>
                        {property.listing_type === "rent" ? "For Rent" : "For Sale"}
                      </Badge>
                      {property.status === "rented" && (
                        <Badge className="bg-orange-500 text-white hover:bg-orange-600">RENTED</Badge>
                      )}
                      {property.status === "sold" && (
                        <Badge className="bg-red-600 text-white hover:bg-red-700">SOLD</Badge>
                      )}
                    </>
                  )}
                  {property.ref_number && (
                    <Badge variant="outline" className="font-mono">{property.ref_number}</Badge>
                  )}
                  <Badge variant="outline" className="capitalize">
                    {property.property_type}
                  </Badge>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Eye className="w-4 h-4" /> {property.views} views
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-serif mb-3" data-testid="property-title">
                  {property.title}
                </h1>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-5 h-5" />
                  <span>{property.address}, {property.city}, {property.state} {property.zip_code}</span>
                </div>
              </div>

              {/* Price and Stats */}
              <div className="bg-card border border-border rounded-sm p-6">
                <p className="font-mono text-4xl font-bold text-primary mb-6" data-testid="property-price">
                  {formatPrice(property.price, property.listing_type)}
                </p>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-2">
                      <BedDouble className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-2xl font-semibold">{property.bedrooms}</p>
                    <p className="text-sm text-muted-foreground">Bedrooms</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-2">
                      <Bath className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-2xl font-semibold">{property.bathrooms}</p>
                    <p className="text-sm text-muted-foreground">Bathrooms</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center mx-auto mb-2">
                      <Square className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-2xl font-semibold">{property.area_sqft.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Sq Ft</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-xl font-serif mb-4">About This Property</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {property.description}
                </p>
              </div>

              {/* Features */}
              {property.features?.length > 0 && (
                <div>
                  <h2 className="text-xl font-serif mb-4">Features & Amenities</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {property.features.map((feature, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center gap-2 p-3 bg-muted rounded-sm"
                      >
                        <Check className="w-4 h-4 text-primary" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Neighborhood Insights */}
              <NeighborhoodInsights city={property.city} state={property.state} address={property.address} />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="sticky top-24 bg-card border border-border rounded-sm p-6">
                <h3 className="font-serif text-xl mb-4">Interested in this property?</h3>
                <Button 
                  onClick={() => setInquiryDialogOpen(true)}
                  className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 mb-4 transition-all"
                  data-testid="inquiry-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Inquiry
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full h-12 active:scale-95 transition-all"
                  data-testid="schedule-tour-btn"
                  onClick={openTourDialog}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Tour
                </Button>

                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-4">
                    Or contact us directly:
                  </p>
                  <a 
                    href={`tel:${businessSettings.phone?.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 text-sm mb-3 hover:text-primary transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {businessSettings.phone}
                  </a>
                  <a 
                    href={`mailto:${businessSettings.email}`}
                    className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {businessSettings.email}
                  </a>
                </div>
              </div>

              {/* Mortgage Calculator CTA */}
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-6">
                <Home className="w-8 h-8 text-primary mb-3" />
                <h4 className="font-serif text-lg mb-2">Calculate Your Mortgage</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  See your estimated monthly payment for this property.
                </p>
                <Link to="/mortgage-calculator" data-testid="mortgage-calc-link">
                  <Button variant="outline" className="w-full">
                    Use Calculator
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Inquiry Dialog */}
      <Dialog open={inquiryDialogOpen} onOpenChange={setInquiryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Send Inquiry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInquirySubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name *</label>
              <Input
                required
                value={inquiryForm.name}
                onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                placeholder="Your name"
                data-testid="inquiry-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <Input
                required
                type="email"
                value={inquiryForm.email}
                onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                placeholder="your@email.com"
                data-testid="inquiry-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Phone</label>
              <Input
                value={inquiryForm.phone}
                onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                placeholder="+1 (555) 000-0000"
                data-testid="inquiry-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Message *</label>
              <Textarea
                required
                value={inquiryForm.message}
                onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                placeholder="I'm interested in this property..."
                rows={4}
                data-testid="inquiry-message"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-11"
              disabled={submitting}
              data-testid="inquiry-submit"
            >
              {submitting ? "Sending..." : "Send Inquiry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tour Scheduling Dialog */}
      <Dialog open={tourDialogOpen} onOpenChange={setTourDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              Schedule a Property Tour
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleTourSubmit} className="space-y-6 mt-4">
            {/* Calendar and Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Calendar */}
              <div>
                <label className="block text-sm font-medium mb-3">Select Date *</label>
                <div className="border border-border rounded-sm p-3">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    modifiers={{
                      booked: (date) => {
                        const dateStr = date.toISOString().split('T')[0];
                        return bookedDates.some(b => b.tour_date === dateStr);
                      }
                    }}
                    modifiersStyles={{
                      booked: { backgroundColor: '#fef3c7', color: '#92400e' }
                    }}
                    className="rounded-md"
                  />
                </div>
                <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-primary"></div>
                    <span>Selected</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-amber-100"></div>
                    <span>Has bookings</span>
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <label className="block text-sm font-medium mb-3">Select Time *</label>
                {!selectedDate ? (
                  <div className="border border-border rounded-sm p-8 text-center text-muted-foreground">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Select a date to see available times</p>
                  </div>
                ) : loadingSlots ? (
                  <div className="border border-border rounded-sm p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-2">Loading available times...</p>
                  </div>
                ) : (
                  <div className="border border-border rounded-sm p-3">
                    <p className="text-sm text-muted-foreground mb-3">
                      {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"].map((time) => {
                        const isBooked = bookedSlots.includes(time);
                        const isSelected = selectedTime === time;
                        return (
                          <button
                            key={time}
                            type="button"
                            disabled={isBooked}
                            onClick={() => setSelectedTime(time)}
                            className={`p-2 text-sm rounded-sm border transition-all ${
                              isBooked 
                                ? "bg-muted text-muted-foreground cursor-not-allowed line-through border-muted" 
                                : isSelected
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "hover:bg-muted border-border hover:border-primary"
                            }`}
                          >
                            {formatTime(time)}
                          </button>
                        );
                      })}
                    </div>
                    {availableSlots.length === 0 && (
                      <p className="text-sm text-destructive mt-3 text-center">
                        All time slots are booked for this date. Please select another date.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-border pt-6">
              <h4 className="text-sm font-medium mb-4">Your Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <Input
                    required
                    value={tourForm.name}
                    onChange={(e) => setTourForm({ ...tourForm, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="tour-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <Input
                    required
                    value={tourForm.phone}
                    onChange={(e) => setTourForm({ ...tourForm, phone: e.target.value })}
                    placeholder="+260 XXX XXX XXX"
                    data-testid="tour-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    required
                    type="email"
                    value={tourForm.email}
                    onChange={(e) => setTourForm({ ...tourForm, email: e.target.value })}
                    placeholder="your@email.com"
                    data-testid="tour-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
                  <Textarea
                    value={tourForm.notes}
                    onChange={(e) => setTourForm({ ...tourForm, notes: e.target.value })}
                    placeholder="Any specific requirements or questions..."
                    rows={2}
                    data-testid="tour-notes"
                  />
                </div>
              </div>
            </div>

            {/* Summary and Submit */}
            {selectedDate && selectedTime && (
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-4">
                <h4 className="font-medium mb-2">Booking Summary</h4>
                <div className="text-sm space-y-1">
                  <p><span className="text-muted-foreground">Property:</span> {property?.title}</p>
                  <p><span className="text-muted-foreground">Date:</span> {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  <p><span className="text-muted-foreground">Time:</span> {formatTime(selectedTime)}</p>
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full h-12"
              disabled={bookingTour || !selectedDate || !selectedTime}
              data-testid="tour-submit"
            >
              {bookingTour ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Tour Booking
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetailPage;

function NeighborhoodInsights({ city, state, address }) {
  const categories = [
    { icon: School, label: "Schools", items: [`${city} International School`, `${city} Academy`, `St. Mary's Primary`], distance: ["0.5 km", "1.2 km", "2.0 km"] },
    { icon: ShoppingBag, label: "Shopping", items: [`${city} Mall`, "Town Center Market", "Corner Supermarket"], distance: ["0.8 km", "1.5 km", "0.3 km"] },
    { icon: Stethoscope, label: "Healthcare", items: [`${city} General Hospital`, "MediCare Clinic", "PharmaCare"], distance: ["2.5 km", "0.7 km", "0.4 km"] },
    { icon: Train, label: "Transport", items: ["Main Bus Terminal", `${city} Train Station`, "Airport Shuttle Point"], distance: ["1.0 km", "3.5 km", "5.0 km"] },
    { icon: TreePine, label: "Parks & Recreation", items: [`${city} Central Park`, "Community Sports Center", "Riverside Walk"], distance: ["0.6 km", "1.8 km", "0.9 km"] },
    { icon: Utensils, label: "Dining", items: ["Various restaurants nearby", "Cafes & bakeries", "Food courts"], distance: ["0.2 km", "0.5 km", "1.0 km"] },
  ];

  return (
    <div data-testid="neighborhood-insights">
      <h2 className="text-xl font-serif mb-4">Neighborhood Insights</h2>
      <p className="text-muted-foreground text-sm mb-4">
        Key amenities near {address ? `${address}, ` : ""}{city}, {state}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {categories.map((cat) => (
          <div key={cat.label} className="bg-muted/50 border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <cat.icon className="w-5 h-5 text-primary" />
              <h3 className="font-medium text-sm">{cat.label}</h3>
            </div>
            <div className="space-y-2">
              {cat.items.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{item}</span>
                  <span className="text-xs bg-background px-2 py-0.5 rounded font-mono">{cat.distance[i]}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-3">* Distances are approximate and may vary.</p>
    </div>
  );
}
